import { useState } from "react";
import { useSearchParams } from "react-router";
import { usePostHog } from "posthog-js/react";
import {
  CONNECTOR_PLATFORM_NAMES,
  ConnectionSidePanel,
  filteredAndSortedDataSourcesNext,
  getAccountingConnectors,
  getConnectionPlatformFromSearchParam,
} from "shared-ui";
import { useCustomerAppConfig } from "@wayflyer/undercarriage";
import type { FundingApplicationSchemas } from "@wayflyer/api-types";
import { useIntl } from "react-intl";

import { COMMON_MESSAGES } from "../../../components/common-messages";
import { getProviderType } from "../../../components/constants";
import { usePlatformConnectors } from "../../../queries/connectors/queries";
import {
  useTriggerValidation,
  useValidatedDocumentQuery,
} from "../../../queries/document-uploads/validated-documents-queries";
import { useFundingApplication, useFundingApplicationMutation } from "../../../queries/funding-application/queries";
import { UploadDocumentType } from "../../../types";

import { AccountingUploadPanel } from "./accounting-upload-panel";

type FileUpload = FundingApplicationSchemas.File | FundingApplicationSchemas.ValidatedFile;

const isValidatedFile = (file: FileUpload): file is FundingApplicationSchemas.ValidatedFile =>
  "validationStatus" in file;

const readyFileIds = (files: FileUpload[]): string[] =>
  files
    .filter(isValidatedFile)
    .filter((f) => f.validationStatus === "ready")
    .map((f) => f.id);

export const ConnectedPlatform = () => {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const [uploadValidationAttempted, setUploadValidationAttempted] = useState(false);
  const [pendingCurrency, setPendingCurrency] = useState<string | undefined>();
  const platform = getConnectionPlatformFromSearchParam(searchParams);
  const posthog = usePostHog();

  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { data: fundingApplication } = useFundingApplication(customerDomain);
  const { data: validatedDocuments } = useValidatedDocumentQuery(
    customerDomain,
    fundingApplication.id,
    platform === "accounting_upload"
  );
  const { mutate: triggerPLValidation } = useTriggerValidation(
    customerDomain,
    fundingApplication.id,
    UploadDocumentType.PROFIT_AND_LOSS
  );
  const { mutate: triggerBSValidation } = useTriggerValidation(
    customerDomain,
    fundingApplication.id,
    UploadDocumentType.BALANCE_SHEET
  );
  const fundingApplicationMutation = useFundingApplicationMutation(customerDomain);
  const { data: connectors } = usePlatformConnectors(customerDomain, { skipConnections: true });
  const filteredDataSources = filteredAndSortedDataSourcesNext(connectors);

  const onlineSalesPlatforms = filteredDataSources.shoppingDataSources;
  const marketingPlatforms = filteredDataSources.marketingDataSources;
  const accountingPlatforms = getAccountingConnectors(filteredDataSources);

  const allPlatforms = [...onlineSalesPlatforms, ...marketingPlatforms, ...accountingPlatforms];
  const isAccountingUploadPanel = platform === "accounting_upload";
  const isPlatformAvailable = platform
    ? allPlatforms.some((p) => p.slug === platform) || isAccountingUploadPanel
    : false;
  const selectedPlatform = allPlatforms.find((p) => p.slug === platform);
  const platformTitle = selectedPlatform?.name || CONNECTOR_PLATFORM_NAMES[platform || ""] || "";

  const panelTitle = isAccountingUploadPanel
    ? "Upload accounting documents"
    : platformTitle
    ? `Connect ${platformTitle}`
    : "";

  const validateAccountingUpload = (): boolean => {
    const plFiles = validatedDocuments?.find((d) => d.documentType === UploadDocumentType.PROFIT_AND_LOSS)?.data ?? [];
    const bsFiles = validatedDocuments?.find((d) => d.documentType === UploadDocumentType.BALANCE_SHEET)?.data ?? [];
    const effectiveCurrency = pendingCurrency ?? fundingApplication.accountingCurrency;
    const missingFileType = plFiles.length === 0 || bsFiles.length === 0;
    const missingCurrency = !effectiveCurrency && !fundingApplication.accountingOptedOut;

    return !missingFileType && !missingCurrency;
  };

  const handleClose = () => {
    const didConnect = searchParams.has("connectionSucceed") || searchParams.has("complete-connection");
    const hadError = searchParams.has("oauth-error");
    if (platform && !isAccountingUploadPanel && !didConnect && !hadError) {
      posthog?.capture("onboarding_connection_abandoned", {
        provider: platform,
        provider_type: getProviderType(platform),
      });
    }
    setUploadValidationAttempted(false);
    setPendingCurrency(undefined);

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete("platform");
    newSearchParams.delete("codat_platform");
    newSearchParams.delete("complete-connection");
    newSearchParams.delete("connectionSucceed");
    newSearchParams.delete("oauth-error");
    setSearchParams(newSearchParams);
  };

  const handleDone = () => {
    if (isAccountingUploadPanel) {
      if (!validateAccountingUpload()) {
        setUploadValidationAttempted(true);
        return;
      }

      const plFiles =
        validatedDocuments?.find((d) => d.documentType === UploadDocumentType.PROFIT_AND_LOSS)?.data ?? [];
      const bsFiles = validatedDocuments?.find((d) => d.documentType === UploadDocumentType.BALANCE_SHEET)?.data ?? [];

      fundingApplicationMutation.mutate(
        {
          id: fundingApplication.id,
          accountingDocumentsRequired: true,
          ...(pendingCurrency && { accountingCurrency: pendingCurrency as FundingApplicationSchemas.Currency }),
        },
        {
          onSuccess: () => {
            posthog?.capture("onboarding_accounting_documents_uploaded", {
              pl_file_count: plFiles.length,
              bs_file_count: bsFiles.length,
            });
          },
        }
      );

      const plIds = readyFileIds(plFiles);
      const bsIds = readyFileIds(bsFiles);
      if (plIds.length > 0) {
        triggerPLValidation(plIds);
      }
      if (bsIds.length > 0) {
        triggerBSValidation(bsIds);
      }
    }

    handleClose();
  };

  return (
    <ConnectionSidePanel
      platform={platform}
      open={isPlatformAvailable}
      title={panelTitle}
      onClose={handleClose}
      primaryAction={{
        content: isAccountingUploadPanel
          ? intl.formatMessage(COMMON_MESSAGES.continueLabel)
          : intl.formatMessage(COMMON_MESSAGES.doneLabel),
        onAction: handleDone,
      }}
      onConnectionStarted={(slug) =>
        posthog?.capture("onboarding_connection_started", {
          provider: slug,
          provider_type: getProviderType(slug),
          method: "oauth",
        })
      }
      renderPanel={(slug) =>
        slug === "accounting_upload" ? (
          <AccountingUploadPanel
            validationAttempted={uploadValidationAttempted}
            selectedCurrency={pendingCurrency ?? fundingApplication.accountingCurrency ?? undefined}
            onCurrencyChange={setPendingCurrency}
          />
        ) : undefined
      }
      codatFallbackPlatformName={fundingApplication.selectedUnsupportedAccounting.join(", ")}
    />
  );
};
